import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface AdminDashboardStats {
  candidaturesCount: number;
  candidaturesPendingCount: number;
  schoolsCount: number;
  postsCount: number;
  activitiesValidatedCount: number;
  paymentsCount: number;
}

export interface CandidatureSummary {
  id: string;
  userId?: string;
  postId?: string;
  status?: string;
  date?: Date;
  description?: string;
  documents?: string[];
}

export interface ActivitySummary {
  id: string;
  candidatureId?: string;
  status?: string;
  date?: Date;
}

export interface PaymentSummary {
  id: string;
  activityId?: string;
  status?: string;
  amount?: number;
  createdAt?: Date;
}

const COLLECTIONS = {
  users: 'users',
  schools: 'schools',
  posts: 'posts',
  candidatures: 'candidatures',
  activities: 'activities',
  payments: 'payments',
} as const;

const STATUSES = {
  pending: 'pending',
  validated: 'validated',
  paid: 'paid',
  rejected: 'rejected',
  inactive: 'inactive',
} as const;

export async function fetchAdminDashboardStats(): Promise<AdminDashboardStats> {
  const [
    candidaturesSnap,
    candidaturesPendingSnap,
    schoolsSnap,
    postsSnap,
    activitiesValidatedSnap,
    paymentsSnap,
  ] = await Promise.all([
    getDocs(collection(db, COLLECTIONS.candidatures)),
    getDocs(
      query(
        collection(db, COLLECTIONS.candidatures),
        where('status', '==', STATUSES.pending)
      )
    ),
    getDocs(collection(db, COLLECTIONS.schools)),
    getDocs(collection(db, COLLECTIONS.posts)),
    getDocs(
      query(
        collection(db, COLLECTIONS.activities),
        where('status', '==', STATUSES.validated)
      )
    ),
    getDocs(collection(db, COLLECTIONS.payments)),
  ]);

  const candidaturesNonRejectedCount = candidaturesSnap.docs.filter((d) => {
    const data = d.data() as any;
    return (data.status ?? STATUSES.pending) !== STATUSES.rejected;
  }).length;
  const schoolsActiveCount = schoolsSnap.docs.filter((d) => {
    const data = d.data() as any;
    return (data.status ?? 'active') !== STATUSES.inactive;
  }).length;
  const postsNonRejectedCount = postsSnap.docs.filter((d) => {
    const data = d.data() as any;
    return (data.status ?? STATUSES.pending) !== STATUSES.rejected;
  }).length;

  return {
    candidaturesCount: candidaturesNonRejectedCount,
    candidaturesPendingCount: candidaturesPendingSnap.size,
    schoolsCount: schoolsActiveCount,
    postsCount: postsNonRejectedCount,
    activitiesValidatedCount: activitiesValidatedSnap.size,
    paymentsCount: paymentsSnap.size,
  };
}

export async function fetchPendingCandidatures(
  limitCount = 10
): Promise<CandidatureSummary[]> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTIONS.candidatures),
      where('status', '==', STATUSES.pending),
      orderBy('date', 'desc'),
      limit(limitCount)
    )
  );

  return snap.docs.map((doc) => {
    const data = doc.data() as any;
    const documentsRaw = data.documents;
    const documents = Array.isArray(documentsRaw)
      ? documentsRaw
          .map((e: unknown) => (typeof e === 'string' ? e.trim() : ''))
          .filter(Boolean)
      : undefined;
    return {
      id: doc.id,
      userId: data.userId,
      postId: data.postId,
      status: data.status,
      date: data.date?.toDate ? data.date.toDate() : undefined,
      description: typeof data.description === 'string' ? data.description : undefined,
      documents: documents?.length ? documents : undefined,
    };
  });
}

export async function fetchActivitiesToValidate(
  limitCount = 10
): Promise<ActivitySummary[]> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTIONS.activities),
      where('status', '==', STATUSES.pending),
      orderBy('date', 'desc'),
      limit(limitCount)
    )
  );

  return snap.docs.map((doc) => {
    const data = doc.data() as any;
    return {
      id: doc.id,
      candidatureId: data.candidatureId,
      status: data.status,
      date: data.date?.toDate ? data.date.toDate() : undefined,
    };
  });
}

export async function fetchPaymentsToPay(
  limitCount = 10
): Promise<PaymentSummary[]> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTIONS.payments),
      where('status', '!=', STATUSES.paid),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    )
  );

  return snap.docs.map((doc) => {
    const data = doc.data() as any;
    return {
      id: doc.id,
      activityId: data.activityId,
      status: data.status,
      amount: typeof data.amount === 'number' ? data.amount : undefined,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
    };
  });
}

